"use client";

import type { ReactNode } from "react";
import { Container, Flex, Spacing, Text, ValueParser } from "@huub-web/ui";
import { useTranslation } from "react-i18next";

import type { MaximumMortgageCalculationResult } from "../../../types/MaximumMortgageWizardResult";
import { ucfirst } from "@huub-web/utils";

export const ValuesListing = ({
  result,
  cta,
}: {
  result: MaximumMortgageCalculationResult;
  cta?: ReactNode;
}) => {
  const { t } = useTranslation();

  return (
    <Container size="s">
      <Flex direction="column" gap="s">
        <Text as="h4" variant="displayM" bold>
          {t(
            `maxMortgageWizard.result.valuesListing.yourData.${result.values?.hasPartner ? "plural" : "singular"}`,
          )}
        </Text>

        {/* Income Section */}
        {result.values?.income && (
          <Flex justify="space-between" gap="xxs">
            <Text italic>
              {t("maxMortgageWizard.result.valuesListing.grossAnnualSalary")}
            </Text>
            <Text>
              <ValueParser
                value={{ value: result.values.income, type: "money" }}
              />
            </Text>
          </Flex>
        )}
        {result.values?.age && (
          <Flex justify="space-between" gap="xxs">
            <Text italic>
              {t("maxMortgageWizard.result.valuesListing.age")}
            </Text>
            <Text>{result.values.age}</Text>
          </Flex>
        )}
        {result.values?.hasPartner && (
          <>
            {result.values?.partnerIncome && (
              <Flex justify="space-between" gap="xxs">
                <Text italic>
                  {t(
                    "maxMortgageWizard.result.valuesListing.partnerGrossAnnualSalary",
                  )}
                </Text>
                <Text>
                  <ValueParser
                    value={{
                      value: result.values.partnerIncome,
                      type: "money",
                    }}
                  />
                </Text>
              </Flex>
            )}
            {result.values?.partnerAge && (
              <Flex justify="space-between" gap="xxs">
                <Text italic>
                  {t("maxMortgageWizard.result.valuesListing.partnerAge")}
                </Text>
                <Text>{result.values.partnerAge}</Text>
              </Flex>
            )}
          </>
        )}

        {/* Current House Section */}
        {(result.values?.hasCurrentHouse ||
          result.values?.purpose === "refinance" ||
          result.values?.purpose === "renovate") && (
          <>
            <Spacing />
            <Text as="h4" bold>
              {t("maxMortgageWizard.result.valuesListing.currentHome")}
            </Text>
            {result.values?.currentHouseMortgageDebt && (
              <Flex justify="space-between" gap="xxs">
                <Text italic>
                  {t("maxMortgageWizard.result.valuesListing.mortgageDebt")}
                </Text>
                <Text>
                  <ValueParser
                    value={{
                      value: result.values.currentHouseMortgageDebt,
                      type: "money",
                    }}
                  />
                </Text>
              </Flex>
            )}
            {result.values?.currentHouseMarketValue && (
              <Flex justify="space-between" gap="xxs">
                <Text italic>
                  {t("maxMortgageWizard.result.valuesListing.marketValue")}
                </Text>
                <Text>
                  <ValueParser
                    value={{
                      value: result.values.currentHouseMarketValue,
                      type: "money",
                    }}
                  />
                </Text>
              </Flex>
            )}
            {result.values?.hasCurrentHouse &&
              result.values?.currentHouseSellingPrice && (
                <Flex justify="space-between" gap="xxs">
                  <Text italic>
                    {t(
                      "maxMortgageWizard.result.valuesListing.expectedSalePrice",
                    )}
                  </Text>
                  <Text>
                    <ValueParser
                      value={{
                        value: result.values.currentHouseSellingPrice,
                        type: "money",
                      }}
                    />
                  </Text>
                </Flex>
              )}
            {result.values?.currentHouseEnergyLabel && (
              <Flex justify="space-between" gap="xxs">
                <Text italic>
                  {t("maxMortgageWizard.result.valuesListing.energyLabel")}
                </Text>
                <Text>{result.values.currentHouseEnergyLabel}</Text>
              </Flex>
            )}
            {result.values?.currentHouseMortgageLender && (
              <Flex justify="space-between" gap="xxs">
                <Text italic>
                  {t("maxMortgageWizard.result.valuesListing.lender")}
                </Text>
                <Text>
                  {result.values.currentHouseMortgageLender === "other"
                    ? t("maxMortgageWizard.lenderOther")
                    : result.values.currentHouseMortgageLender}
                </Text>
              </Flex>
            )}
          </>
        )}

        {/* Financial Obligations Section */}
        {(result.values?.hasLoans ||
          result.values?.hasStudentDebt ||
          result.values?.hasPrivateLease) && (
          <>
            <Spacing />
            <Text as="h4" bold>
              {t("maxMortgageWizard.result.valuesListing.financialObligations")}
            </Text>
            {result.values?.hasLoans && (
              <>
                {result.values?.loans && (
                  <Flex justify="space-between" gap="xxs">
                    <Text italic>
                      {t("maxMortgageWizard.result.valuesListing.loans")}
                    </Text>
                    <Text>
                      <ValueParser
                        value={{
                          value: result.values.loans,
                          type: "money",
                        }}
                      />
                    </Text>
                  </Flex>
                )}
                {result.values?.loansPartner && (
                  <Flex justify="space-between" gap="xxs">
                    <Text italic>
                      {t("maxMortgageWizard.result.valuesListing.partnerLoans")}
                    </Text>
                    <Text>
                      <ValueParser
                        value={{
                          value: result.values.loansPartner,
                          type: "money",
                        }}
                      />
                    </Text>
                  </Flex>
                )}
              </>
            )}
            {result.values?.hasStudentDebt && (
              <>
                {result.values?.studentDebt && (
                  <Flex justify="space-between" gap="xxs">
                    <Text italic>
                      {t("maxMortgageWizard.result.valuesListing.studentDebt")}
                    </Text>
                    <Text>
                      <ValueParser
                        value={{
                          value: result.values.studentDebt,
                          type: "money",
                        }}
                      />
                    </Text>
                  </Flex>
                )}
                {result.values?.studentDebtPartner && (
                  <Flex justify="space-between" gap="xxs">
                    <Text italic>
                      {t(
                        "maxMortgageWizard.result.valuesListing.partnerStudentDebt",
                      )}
                    </Text>
                    <Text>
                      <ValueParser
                        value={{
                          value: result.values.studentDebtPartner,
                          type: "money",
                        }}
                      />
                    </Text>
                  </Flex>
                )}
              </>
            )}
            {result.values?.hasPrivateLease && (
              <>
                {result.values?.privateLease && (
                  <Flex justify="space-between" gap="xxs">
                    <Text italic>
                      {t("maxMortgageWizard.result.valuesListing.privateLease")}
                    </Text>
                    <Text>
                      <ValueParser
                        value={{
                          value: result.values.privateLease,
                          type: "money",
                        }}
                      />
                    </Text>
                  </Flex>
                )}
                {result.values?.privateLeasePartner && (
                  <Flex justify="space-between" gap="xxs">
                    <Text italic>
                      {t(
                        "maxMortgageWizard.result.valuesListing.partnerPrivateLease",
                      )}
                    </Text>
                    <Text>
                      <ValueParser
                        value={{
                          value: result.values.privateLeasePartner,
                          type: "money",
                        }}
                      />
                    </Text>
                  </Flex>
                )}
              </>
            )}
          </>
        )}

        {result.values?.ownCapital && (
          <Flex justify="space-between" gap="xxs">
            <Text italic>
              {t("maxMortgageWizard.result.valuesListing.ownFunds")}
            </Text>
            <Text>
              <ValueParser
                value={{ value: result.values.ownCapital, type: "money" }}
              />
            </Text>
          </Flex>
        )}

        {/* New House Section */}
        {result.values?.purpose !== "refinance" &&
          (result.values?.walterAddressObjectID ||
            result.values?.newHousePrice ||
            result.values?.newHouseEnergyLabel) && (
            <>
              <Spacing />
              <Text as="h4" bold>
                {ucfirst(
                  t(
                    `maxMortgageWizard.result.subject.${result.values?.purpose === "renovate" ? "renovation" : "house"}`,
                  ),
                )}
              </Text>

              {result.values?.newHousePrice &&
                result.values.newHousePrice !== "0" && (
                  <Flex justify="space-between" gap="xxs">
                    <Text italic>
                      {t(
                        result.values?.purpose === "renovate" ||
                          result.values?.houseInMind === "no"
                          ? "maxMortgageWizard.result.financing.desiredLoanLabel"
                          : result.values?.houseInMind === "newly-build"
                            ? "maxMortgageWizard.result.financing.neededMortgageLabel"
                            : "maxMortgageWizard.result.financing.askingPriceLabel",
                      )}
                    </Text>
                    <Text>
                      <ValueParser
                        value={{
                          value: result.values.newHousePrice,
                          type: "money",
                        }}
                      />
                    </Text>
                  </Flex>
                )}

              {result.values?.newHouseEnergyLabel &&
                result.values.newHouseEnergyLabel !== "unknown" && (
                  <Flex justify="space-between" gap="xxs">
                    <Text italic>
                      {t("maxMortgageWizard.result.valuesListing.energyLabel")}
                    </Text>
                    <Text>{result.values.newHouseEnergyLabel}</Text>
                  </Flex>
                )}
            </>
          )}
      </Flex>
      {cta}
    </Container>
  );
};
